// Матрица прав — одна на сервис (FR-015): строки 001, 002, 003, 005 и следующих срезов; колонки —
// по срезам 040 и 041 (specs/041-roles-ownership/contracts/rights-matrix.md): Администратор,
// Сотрудник (неявная роль), Владелец (lead — права владельца в своих протоколах), Сопровождение.
// Действие разрешено, если хотя бы одна действующая роль пользователя разрешает операцию для
// записи (FR-011). Всё, чего нет в матрице, запрещено. Сверх ячеек действует правило «в сеансе»
// (FR-011а): выход, свой профиль и свои уведомления — любому вошедшему. 041: замещения нет.
import { z } from '../kernel/zod.ts';
import type { ProtocolRole } from '../minutes/access.ts';
import { roleInfo, roleLayers, roles } from './roles.ts';
import type { Role, RoleLayer } from './roles.ts';

/**
 * Правило области:
 * - all — во всей установке («всё»);
 * - areas — в своих протоколах: владельца и ролей в протоколе («свои протоколы»; 041 — областей
 *   доступа нет, протоколы владельца даёт access.protocol_owners);
 * - open — во всей установке, кроме протоколов с общим доступом «Только добавленные» («открытые»,
 *   020: research, Р2);
 * - self — только своя запись («сам»);
 * - grant — только при действующем временном доступе сопровождения («по доступу»);
 * - none — запрещено («—»).
 */
export const scopeRules = ['all', 'open', 'areas', 'self', 'grant', 'none'] as const;

export type ScopeRule = (typeof scopeRules)[number];

export const ScopeRuleSchema = z.enum(scopeRules);

export const scopeRuleLabels = {
  all: 'всё',
  open: 'открытые',
  areas: 'свои протоколы',
  self: 'сам',
  grant: 'по доступу',
  none: '—',
} as const satisfies Record<ScopeRule, string>;

/** Ячейка матрицы: правило с уточнением или ссылка «как …» на другие операции. */
export type MatrixCell =
  | { readonly rule: ScopeRule; readonly note?: string }
  | { readonly rule: 'same_as'; readonly sameAs: readonly string[]; readonly label: string };

type CellInput = MatrixCell | ScopeRule;
type RowCells = readonly [CellInput, CellInput, CellInput, CellInput];

export interface MatrixRow<O extends string = string> {
  readonly operations: readonly O[];
  /** Пояснение строки, если оно есть в документе: «список и карточка». */
  readonly description?: string;
  readonly cells: Readonly<Record<Role, MatrixCell>>;
}

/**
 * Ячейки — в порядке колонок: Администратор (client_admin), Сотрудник (member, неявная),
 * Владелец (lead — права в своих протоколах, 041: строка access.protocol_owners), Сопровождение
 * (support). 040: колонки
 * контролёра, председателя и наблюдателя удалены; администратор — объединение прав
 * администратора клиента и контролёра (contracts/rights-matrix.md 040).
 */
function row<const O extends readonly string[]>(
  operations: O,
  cells: RowCells,
  description?: string,
): {
  readonly operations: O;
  readonly description?: string;
  readonly cells: Readonly<Record<Role, MatrixCell>>;
} {
  const normalized = Object.fromEntries(
    roles.map((role, i) => {
      const cell = cells[i] ?? 'none';
      return [role, typeof cell === 'string' ? { rule: cell } : cell];
    }),
  ) as Record<Role, MatrixCell>;
  return description === undefined
    ? { operations, cells: normalized }
    : { operations, description, cells: normalized };
}

const asAuditRead = {
  rule: 'same_as',
  sameAs: ['audit.read.content', 'audit.read.access'],
  label: 'как audit.read.*',
} as const;

export const rightsMatrix = [
  row(['auth.session'], ['self', 'self', 'self', 'self'], 'вход, выход, выход на всех устройствах'),
  row(['profile.read', 'profile.change_password'], ['self', 'self', 'self', 'self']),
  row(['users.read'], ['all', 'none', 'none', 'none'], 'список и карточка'),
  row(['users.create', 'users.update'], ['all', 'none', 'none', 'none']),
  row(['users.archive', 'users.restore'], ['all', 'none', 'none', 'none']),
  row(
    ['users.invite', 'users.reset_password', 'users.unlock', 'users.revoke_sessions'],
    ['all', 'none', 'none', 'none'],
  ),
  row(['assignments.read'], ['all', 'self', 'self', 'self']),
  row(['assignments.manage'], ['all', 'none', 'none', 'none'], 'назначить и снять роль'),
  row(['support.read'], ['all', 'none', 'none', 'self']),
  row(['support.grant', 'support.revoke'], ['all', 'none', 'none', 'none']),
  row(['rights.read'], ['all', 'none', 'none', 'none']),
  // 040 (research Р4): сотрудник читает записи по открытым протоколам, как прежний наблюдатель.
  row(['audit.read.content'], ['all', 'open', 'areas', 'grant'], 'записи по документам и пунктам'),
  row(['audit.read.access'], ['all', 'none', 'none', 'none'], 'записи о пользователях и доступе'),
  row(['audit.export'], [asAuditRead, asAuditRead, asAuditRead, 'grant']),
  row(['notifications.read'], ['self', 'self', 'self', 'self']),
  row(['ops.read'], ['all', 'none', 'none', 'all'], 'выкатки и проверки восстановления'),
  // 002: экраны сопровождения. Технический журнал, задачи, оповещения и метрики — без
  // персональных данных: временный доступ сопровождению не нужен. Администратор видит
  // только краткую панель.
  row(['ops.health.read'], ['all', 'none', 'none', 'all'], 'краткая панель состояния'),
  row(['ops.status.read'], ['none', 'none', 'none', 'all'], 'панель состояния'),
  row(['ops.jobs.read'], ['none', 'none', 'none', 'all'], 'фоновые задачи'),
  row(['ops.jobs.retry'], ['none', 'none', 'none', 'all'], 'повторить задачу'),
  row(['ops.jobs.cancel'], ['none', 'none', 'none', 'all'], 'отменить задачу'),
  row(['ops.log.read'], ['none', 'none', 'none', 'all'], 'технический журнал'),
  row(['ops.alerts.read'], ['none', 'none', 'none', 'all'], 'оповещения'),
  row(['ops.backups.read'], ['none', 'none', 'none', 'all'], 'копии и проверки восстановления'),
  row(['ops.metrics.read'], ['none', 'none', 'none', 'all'], 'метрики'),
  // 003: документы и таблица открытых пунктов. 040: протокол и папку создаёт любой сотрудник
  // (FR-007); создатель протокола — владелец (023).
  row(
    ['documents.manage'],
    ['all', 'none', 'areas', 'none'],
    'папки, свойства, перемещение и корзина протоколов',
  ),
  row(['documents.create'], ['all', 'all', 'none', 'none'], '«Новый протокол» и папка'),
  row(['documents.versions.upload'], ['all', 'none', 'areas', 'none'], '«Новая версия»'),
  // 020: сотрудник читает протоколы, кроме «Только добавленные» (общий доступ, research Р2).
  row(['documents.read'], ['all', 'open', 'areas', 'grant'], 'список, оригинал, лист контроля'),
  row(['control.read'], ['all', 'open', 'areas', 'grant'], 'таблица открытых пунктов'),
  // 005: справочник, настройки, ключи агента. Область владельца — записи, упомянутые в его
  // протоколах. 040: сотрудник читает весь справочник (research Р4).
  row(
    ['directory.read'],
    ['all', 'all', 'areas', 'grant'],
    'справочник, упоминания, пары, «К разбору», поиск',
  ),
  row(
    ['directory.write'],
    ['all', 'none', 'areas', 'none'],
    'создать и дополнить запись, почта, места работы, кураторы, зона, псевдонимы, предложить пару',
  ),
  row(
    ['directory.decide'],
    ['all', 'none', 'none', 'none'],
    'пары, склейка, разъединение, подтверждение адреса, «получает письма»',
  ),
  row(['settings.write'], ['all', 'none', 'none', 'none'], 'настройки установки'),
  row(['agent_keys.manage'], ['all', 'none', 'none', 'none'], 'ключи агента'),
  // 008: канал почты.
  row(['mail.read'], ['all', 'none', 'none', 'all'], 'подключение почты, письма и ответные письма'),
  row(
    ['mail.write'],
    ['all', 'none', 'none', 'all'],
    'подключение почты, ключ, проверка, отчёты о доставке, пробное письмо, отключение отправки',
  ),
  // 042: «Настройки → Шаблоны писем» — слова писем каталога правит только администратор.
  row(
    ['mail.templates'],
    ['all', 'none', 'none', 'none'],
    'шаблоны писем: список, правка, предпросмотр, история',
  ),
  // 010: схемы шаблонов и мастер подключения.
  row(
    ['templates.read'],
    ['all', 'none', 'none', 'none'],
    'схемы шаблонов, карта колонок, история версий',
  ),
  row(
    ['templates.write'],
    ['all', 'none', 'none', 'none'],
    'мастер подключения, правка, версии, архив и возврат схем',
  ),
  // 011: протокол в системе.
  row(['minutes.read'], ['all', 'open', 'areas', 'grant'], 'таблица протокола в режиме ведения'),
  row(
    // 018: анализ дополнительно ограничен инициатором в обработчике.
    ['minutes.edit'],
    ['all', 'none', 'areas', 'none'],
    'команды таблицы: правка ячеек, новая строка, решение родителя',
  ),
  // 040 (FR-008): перевод своего протокола и возврат в режим файла — владелец.
  row(
    ['minutes.transfer'],
    ['all', 'none', 'areas', 'none'],
    'перевод протокола и возврат в режим файла',
  ),
  // 020: доступ к протоколу.
  row(
    ['minutes.manage'],
    ['all', 'none', 'areas', 'none'],
    'команды протокола целиком: доп. поля, «Скрыть в протоколе», снимок недели',
  ),
  row(['minutes.access.read'], ['all', 'open', 'areas', 'grant'], 'список доступа к протоколу'),
  row(
    ['minutes.access.manage'],
    ['all', 'none', 'areas', 'none'],
    'выдать, сменить и снять роль в протоколе, общий доступ, пригласить',
  ),
  // 030: «Мои задачи» и «Первое письмо». items.own — ещё и правило «в сеансе»: его даёт связь
  // учётной записи с человеком справочника, а не роль; без связи — ACC-008.
  row(
    ['items.own'],
    ['self', 'self', 'self', 'none'],
    '«свои пункты»: «Мои задачи», ответы и правка своих пунктов',
  ),
  row(
    ['minutes.first_letter'],
    ['all', 'none', 'areas', 'none'],
    '«Отправить первое письмо…»: запланировать и отменить',
  ),
  // 033: стоп-кран и рассылка протокола. 040 (FR-008): включение рассылки — владелец.
  row(['mail.stop'], ['all', 'none', 'none', 'none'], '«Остановить все сообщения» и «Возобновить»'),
  row(
    ['minutes.mailing'],
    ['all', 'none', 'areas', 'none'],
    '«Рассылка протокола…»: просмотр, «Включить в тени», «Выключить рассылку»',
  ),
  row(
    ['minutes.mailing_enable'],
    ['all', 'none', 'areas', 'none'],
    '«Включить рассылку» протокола',
  ),
  // 032: право руководителя (поправка XII 2.7.0; ADR-0047). items.team — правило «в сеансе»,
  // как items.own: его дают связь учётной записи с человеком и кураторство в справочнике.
  row(
    ['items.team'],
    ['self', 'self', 'self', 'none'],
    '«Просрочено у ваших сотрудников»: только чтение, только просроченные пункты людей, у которых пользователь — руководитель по дереву (поправка XII 2.7.0)',
  ),
  row(
    ['people.away'],
    ['all', 'none', 'none', 'none'],
    '«В отъезде до» другому человеку (вкладка «Ответственные»)',
  ),
] as const;

export type Operation = (typeof rightsMatrix)[number]['operations'][number];

export const operations: readonly Operation[] = rightsMatrix.flatMap((r) => r.operations);

export const OperationSchema = z.enum(operations);

const NONE: MatrixCell = { rule: 'none' };

const cellsByOperation = new Map<string, Readonly<Record<Role, MatrixCell>>>(
  rightsMatrix.flatMap((r) => r.operations.map((op) => [op, r.cells] as const)),
);

export function isOperation(value: string): value is Operation {
  return cellsByOperation.has(value);
}

/**
 * Правило «в сеансе» (FR-011а; contracts/rights-matrix.md): эти операции над своим — выход,
 * профиль и смена пароля, свои уведомления — даёт сам сеанс, любому пользователю, даже без ролей.
 * Правило — «сам»; ячейки матрицы оно не меняет, а дополняет: у кого правило шире («всё»), тот
 * остаётся при нём.
 */
export const sessionOperations = [
  'auth.session',
  'profile.read',
  'profile.change_password',
  'notifications.read',
  // 030 (research Р2): «свои пункты» — у сеанса со связью с человеком; без связи — ACC-008.
  'items.own',
  // 032: «Просрочено у ваших сотрудников» — у сеанса со связью с человеком-руководителем.
  'items.team',
] as const satisfies readonly Operation[];

/**
 * 030 (research Р1; ADR-0044): что даёт сеанс по ссылке — только эти операции; ролей установки,
 * ролей в протоколах и общего доступа у него нет.
 */
export const linkSessionOperations = [
  'auth.session',
  'profile.read',
  // Свои уведомления: колокольчик шапки по «Табелю» (конституция X), каркас тот же.
  'notifications.read',
  'items.own',
  // 032: руководитель входит по ссылке из копии руководителю (FR-021).
  'items.team',
] as const satisfies readonly Operation[];

const linkSessionOperationSet: ReadonlySet<string> = new Set(linkSessionOperations);

/** Даёт ли сеанс по ссылке эту операцию (030). */
export function isLinkSessionOperation(operation: Operation): boolean {
  return linkSessionOperationSet.has(operation);
}

const sessionOperationSet: ReadonlySet<string> = new Set(sessionOperations);

export function isSessionOperation(operation: Operation): boolean {
  return sessionOperationSet.has(operation);
}

/** Правило, которое даёт сам сеанс: «сам» для операций «в сеансе», иначе «—». */
export function sessionRuleFor(operation: Operation): ScopeRule {
  return isSessionOperation(operation) ? 'self' : 'none';
}

/** Ячейка матрицы как есть; нет строки или роли — «—». */
export function cellFor(role: Role, operation: Operation): MatrixCell {
  const cells = cellsByOperation.get(operation);
  if (!cells || !Object.hasOwn(cells, role)) return NONE;
  return cells[role];
}

/** Подпись ячейки как в документе: «сам (своя роль)», «как audit.read.*». */
export function cellLabel(cell: MatrixCell): string {
  if (cell.rule === 'same_as') return cell.label;
  const label = scopeRuleLabels[cell.rule];
  return cell.note ? `${label} (${cell.note})` : label;
}

const WIDTH: Readonly<Record<ScopeRule, number>> = {
  none: 0,
  self: 1,
  grant: 2,
  areas: 3,
  open: 4,
  all: 5,
};

/** Самое широкое правило из набора; пустой набор — «—». */
export function widestRule(rules: Iterable<ScopeRule>): ScopeRule {
  let widest: ScopeRule = 'none';
  for (const rule of rules) if (WIDTH[rule] > WIDTH[widest]) widest = rule;
  return widest;
}

/** Действующая роль пользователя для проверки прав (041: замещения нет — только своя роль). */
export type RoleGrant = Role;

/**
 * Правило действующей роли для операции, без обращения к базе. Для ячейки «как audit.read.*» —
 * самое широкое из правил тех операций: оно решает, доступна ли операция вообще; какие записи
 * попадут в выгрузку, решают правила audit.read.content и audit.read.access по чувствительности
 * записи (auditReadOperation).
 */
export function grantRuleFor(grant: RoleGrant, operation: Operation): ScopeRule {
  const cell = cellFor(grant, operation);
  if (cell.rule === 'same_as') {
    return widestRule(cell.sameAs.filter(isOperation).map((op) => grantRuleFor(grant, op)));
  }
  return cell.rule;
}

/** Правило собственной роли для операции (ячейка матрицы; «как …» — самое широкое из правил). */
export function ruleFor(role: Role, operation: Operation): ScopeRule {
  return grantRuleFor(role, operation);
}

/**
 * Разные разрешающие правила ролей пользователя для операции и правило «в сеансе» (FR-011а);
 * пусто — запрещено.
 */
export function rulesFor(userRoles: readonly RoleGrant[], operation: Operation): ScopeRule[] {
  const rules = new Set<ScopeRule>();
  for (const grant of userRoles) {
    const rule = grantRuleFor(grant, operation);
    if (rule !== 'none') rules.add(rule);
  }
  const session = sessionRuleFor(operation);
  if (session !== 'none') rules.add(session);
  return [...rules];
}

/** Разрешает ли хотя бы одна роль (или сам сеанс) операцию хоть в какой-то области. */
export function isAllowed(userRoles: readonly RoleGrant[], operation: Operation): boolean {
  return rulesFor(userRoles, operation).length > 0;
}

export type Permissions = Partial<Record<Operation, ScopeRule>>;

/**
 * Разрешённые операции и самое широкое правило по ним — для меню и «Создать ▾»
 * (ответ GET /api/auth/me). Операции «в сеансе» есть у всех (FR-011а). Сервер всё равно
 * проверяет каждое действие по ролям и протоколам.
 */
export function effectivePermissions(
  userRoles: readonly RoleGrant[],
  protocol: ProtocolGrants = NO_PROTOCOL_GRANTS,
): Permissions {
  const result: Permissions = {};
  for (const op of operations) {
    const rules = rulesFor(userRoles, op);
    // 020: роль в протоколе даёт операцию в протоколах из списка — как «области»; общий доступ —
    // во всех протоколах, кроме «Только добавленные» (research, Р3).
    // 022: внешнему роль в протоколе даёт только externalProtocolOperations, общего доступа нет.
    const byRole = !protocol.external || isExternalProtocolOperation(op);
    if (byRole && protocol.roles.some((role) => protocolRuleFor(role, op) !== 'none')) {
      rules.push('areas');
    }
    if (protocol.company && !protocol.external && protocolRuleFor('company', op) !== 'none') {
      rules.push('open');
    }
    const rule = widestRule(rules);
    if (rule !== 'none') result[op] = rule;
  }
  return result;
}

// --- Роли в протоколе (020: contracts/api.md, «Матрица прав»; research, Р2; ADR-0039) ---

/**
 * Правило роли в протоколе: protocol — протокол целиком; zone — пункты зоны редактора (сервер
 * проверяет каждый затронутый пункт); none — запрещено.
 */
export const protocolRules = ['protocol', 'zone', 'none'] as const;
export type ProtocolRule = (typeof protocolRules)[number];

/**
 * Кому правило второй таблицы: роли в протоколе и company — общий доступ «Все сотрудники компании —
 * читатели» (любой пользователь в сеансе без роли «Сопровождение», протокол не «Только
 * добавленные»). 041: zone_editor — не роль, а «Редактор» с непустой зоной (protocolGranteeOf):
 * роль одна, правило зоны — у редактора с зоной.
 */
export const protocolGrantees = ['reader', 'editor', 'zone_editor', 'company'] as const;
export type ProtocolGrantee = (typeof protocolGrantees)[number];
/** Роль в протоколе для правил: читатель, редактор, редактор с зоной. */
export type ProtocolRuleRole = Exclude<ProtocolGrantee, 'company'>;

/**
 * 041 (research Р7): по роли и зоне выдачи — кому правило второй таблицы. «Редактор» с непустой
 * зоной — zone_editor; старое значение zone_editor из базы и событий — тоже.
 */
export function protocolGranteeOf(
  role: ProtocolRole | 'zone_editor',
  zone: readonly unknown[] | null | undefined,
): ProtocolRuleRole {
  if (role === 'zone_editor') return 'zone_editor';
  return role === 'editor' && (zone?.length ?? 0) > 0 ? 'zone_editor' : role;
}
type ProtocolCells = Readonly<Record<ProtocolGrantee, ProtocolRule>>;

/** Таблица ролей в протоколе: строки — операции протокола, ячейки — правила. */
export const protocolRightsMatrix: readonly {
  readonly operations: readonly Operation[];
  readonly cells: ProtocolCells;
}[] = [
  {
    operations: ['documents.read', 'control.read', 'minutes.read', 'minutes.access.read'],
    cells: { reader: 'protocol', editor: 'protocol', zone_editor: 'protocol', company: 'protocol' },
  },
  {
    operations: ['minutes.edit'],
    cells: { reader: 'none', editor: 'protocol', zone_editor: 'zone', company: 'none' },
  },
  {
    operations: ['minutes.manage'],
    cells: { reader: 'none', editor: 'protocol', zone_editor: 'none', company: 'none' },
  },
  {
    // 030: «Первое письмо» — как управление доступом: ведущему (владельцу) по области, роли
    // в протоколе его не дают (contracts/api.md 030, «Матрица прав»).
    // 033: рассылка протокола — так же: роли в протоколе её не дают.
    operations: [
      'minutes.access.manage',
      'minutes.transfer',
      'minutes.first_letter',
      'minutes.mailing',
      'minutes.mailing_enable',
    ],
    cells: { reader: 'none', editor: 'none', zone_editor: 'none', company: 'none' },
  },
];

const protocolCellsByOperation = new Map<string, ProtocolCells>(
  protocolRightsMatrix.flatMap((r) => r.operations.map((op) => [op, r.cells] as const)),
);

/** Операции протокола: их ядро прав проверяет и по ролям в протоколе и общему доступу. */
export const protocolOperations: readonly Operation[] = protocolRightsMatrix.flatMap(
  (r) => r.operations,
);

export function isProtocolOperation(operation: Operation): boolean {
  return protocolCellsByOperation.has(operation);
}

/** Правило второй таблицы; операции нет в таблице — «—». */
export function protocolRuleFor(grantee: ProtocolGrantee, operation: Operation): ProtocolRule {
  return protocolCellsByOperation.get(operation)?.[grantee] ?? 'none';
}

/** Роли пользователя в протоколах и общий доступ — для effectivePermissions (меню, /api/auth/me). */
export interface ProtocolGrants {
  /** Роли по правилам: «Редактор» с зоной — zone_editor (protocolGranteeOf). */
  readonly roles: readonly ProtocolRuleRole[];
  /** Общий доступ «Все сотрудники» действует (пользователь без роли «Сопровождение»). */
  readonly company: boolean;
  /** 022: внешний пользователь — роли в протоколе дают только externalProtocolOperations. */
  readonly external?: boolean;
}

const NO_PROTOCOL_GRANTS: ProtocolGrants = { roles: [], company: false };

// --- Внешний пользователь (022: contracts/api.md, «Матрица прав»; research, Р3; ADR-0040) ---

/**
 * Что роль в протоколе даёт внешнему пользователю — только сам протокол: чтение, правка по роли
 * и команды протокола целиком у редактора. «Документы», «Контроль» и список доступа — нет.
 * Роли на установку и общий доступ «Все сотрудники» внешнему не действуют.
 */
export const externalProtocolOperations = [
  'minutes.read',
  'minutes.edit',
  'minutes.manage',
] as const satisfies readonly Operation[];

const externalOperationSet: ReadonlySet<string> = new Set(externalProtocolOperations);

/** Даёт ли роль в протоколе эту операцию внешнему пользователю. */
export function isExternalProtocolOperation(operation: Operation): boolean {
  return externalOperationSet.has(operation);
}

/** Какая операция чтения журнала относится к записи с этой чувствительностью. */
export function auditReadOperation(sensitivity: 'access' | 'content'): Operation {
  return sensitivity === 'access' ? 'audit.read.access' : 'audit.read.content';
}

/** Колонки таблицы «Роли в компании» на экране «Роли и права» (040, FR-011): без владельца. */
export const companyColumns = [
  'client_admin',
  'member',
  'support',
] as const satisfies readonly Role[];

/**
 * Колонки таблицы «Роли в протоколе» (040, FR-019; 041, FR-007): владелец — ячейки lead
 * по операциям протокола; у «Редактора» правило с зоной — пометкой в ячейке (zone).
 */
export const rightsProtocolColumns = ['owner', 'editor', 'reader', 'company'] as const;
export type RightsProtocolColumn = (typeof rightsProtocolColumns)[number];

export const rightsProtocolColumnTitles = {
  owner: 'Владелец',
  editor: 'Редактор',
  reader: 'Читатель',
  company: 'Все сотрудники',
} as const satisfies Record<RightsProtocolColumn, string>;

const protocolRuleLabels = {
  protocol: 'весь протокол',
  zone: 'пункты зоны',
  none: '—',
} as const satisfies Record<ProtocolRule, string>;

/** Правило владельца для строки второй таблицы: ячейки lead по её операциям («области» — протокол). */
function ownerRule(operations: readonly Operation[]): ProtocolRule {
  return operations.every((op) => ruleFor('lead', op) !== 'none') ? 'protocol' : 'none';
}

type CellView = { rule: ScopeRule | 'same_as'; label: string; note?: string; sameAs?: Operation[] };

/** Ячейка второй таблицы; zone — правило «Редактора» с зоной, если оно уже (041). */
type ProtocolCellView = {
  rule: ProtocolRule;
  label: string;
  zone?: { rule: ProtocolRule; label: string };
};

/**
 * Данные для GET /api/rights (040, FR-019): роли с пояснениями (колонки «Роли в компании»), слои
 * ролей для вкладки «Роли», матрица с подписями ячеек всех колонок (lead — для владельца),
 * таблица ролей в протоколе и операции правила «в сеансе» (FR-011а) — их даёт сам сеанс.
 */
export function rightsPayload(): {
  roles: { role: Role; title: string; description: string }[];
  layers: {
    key: RoleLayer['key'];
    title: string;
    roles: { key: string; title: string; description: string }[];
  }[];
  inSession: Operation[];
  matrix: { operations: Operation[]; description?: string; cells: Record<Role, CellView> }[];
  protocolMatrix: {
    operations: Operation[];
    cells: Record<RightsProtocolColumn, ProtocolCellView>;
  }[];
} {
  return {
    roles: companyColumns.map((role) => ({
      role,
      title: roleInfo[role].title,
      description: roleInfo[role].description,
    })),
    layers: roleLayers.map((layer) => ({
      key: layer.key,
      title: layer.title,
      roles: layer.roles.map((r) => ({ ...r })),
    })),
    inSession: [...sessionOperations],
    matrix: rightsMatrix.map((r) => {
      const cells = Object.fromEntries(
        roles.map((role) => {
          const cell = r.cells[role];
          const view: CellView =
            cell.rule === 'same_as'
              ? { rule: cell.rule, label: cell.label, sameAs: cell.sameAs.filter(isOperation) }
              : cell.note
                ? { rule: cell.rule, label: cellLabel(cell), note: cell.note }
                : { rule: cell.rule, label: cellLabel(cell) };
          return [role, view];
        }),
      ) as Record<Role, CellView>;
      const operationsOfRow: Operation[] = [...r.operations];
      return r.description === undefined
        ? { operations: operationsOfRow, cells }
        : { operations: operationsOfRow, description: r.description, cells };
    }),
    protocolMatrix: protocolRightsMatrix.map((r) => {
      const rules: Record<RightsProtocolColumn, ProtocolRule> = {
        owner: ownerRule(r.operations),
        editor: r.cells.editor,
        reader: r.cells.reader,
        company: r.cells.company,
      };
      const cells = Object.fromEntries(
        rightsProtocolColumns.map((col) => {
          const view: ProtocolCellView = {
            rule: rules[col],
            label: protocolRuleLabels[rules[col]],
          };
          // 041 (FR-007): у «Редактора» с зоной правило уже — пометка в ячейке редактора.
          if (col === 'editor' && r.cells.zone_editor !== r.cells.editor) {
            const zone = r.cells.zone_editor;
            return [
              col,
              {
                ...view,
                label: `${view.label}; с зоной — ${zone === 'none' ? 'нет' : protocolRuleLabels[zone]}`,
                zone: { rule: zone, label: protocolRuleLabels[zone] },
              },
            ];
          }
          return [col, view];
        }),
      ) as Record<RightsProtocolColumn, ProtocolCellView>;
      return { operations: [...r.operations], cells };
    }),
  };
}
